// HR self-serve signup contracts — slice 10, DESIGN §6.6. Shared between web
// and api so the click-through form validates client-side against the same
// schema the server enforces.
//
// PROJECT §4 Step 1 (assisted self-serve onboarding) — Step 1 captures
// organisation name, headcount band, and the primary + escalation contacts
// (name + work email) so Maeve + the HR shell can route red-flag and case
// notifications before the org provisions a full user roster.

import { z } from 'zod';

// Versioned placeholder. Real DPA wording is gated on legal counsel sign-off
// per [[PROJECT.md §6.2]] — when the revision lands, bump this value and the
// activation gate at `/auth/verify` will pin the new version on any signup
// from then on. Existing orgs keep the version they accepted.
export const DPA_PLACEHOLDER_VERSION = '0.1.0-placeholder';

// Body copy is intentionally legally non-binding placeholder — labelled as such
// in the UI. Replace before any production pilot per DESIGN §6.6.
export const DPA_PLACEHOLDER_TEXT = `besideU placeholder Data Processing Agreement v${DPA_PLACEHOLDER_VERSION}. This is placeholder language pending legal-counsel review; it is not the final DPA. By clicking "Accept", you confirm you have read this placeholder and understand that the final DPA will replace it before any production-pilot data is processed.`;

export const HR_SAFETY_CONTACT_ACKNOWLEDGEMENT_TEXT = `As the first HR Admin for this organisation, you will be the named recipient of any red-flag notifications generated by Maeve (per DESIGN §3.3.4 — Article 9 special category data). You can reassign this role to a different HR Admin from the HR shell after signup.`;

// Slice 31 — `dpaVersionAccepted` is now a generic semver-shaped string
// rather than a `z.literal(DPA_PLACEHOLDER_VERSION)` pin. The signup
// endpoint validates the sent version equals the currently-active DPA
// row's version (sourced from `system_prompts` mode 'DPA' with code-
// constant fallback), so a client sending a stale cached version gets
// a clear error rather than a generic invalid_body.
const dpaVersionShape = z.string().trim().min(1).max(120);

// PROJECT §4 Step 1 — headcount band. Free-text size is intentionally absent:
// the value drives aggregate UI (manager dashboards, Maeve tone) and audit
// thresholds, and a typo'd "a thousand" can't be reasoned about. The bands
// map to the canonical UK workforce brackets in `headcountBandSize()`.
export const HEADCOUNT_BANDS = ['1-9', '10-49', '50-249', '250-999', '1000+'] as const;
export type HeadcountBand = (typeof HEADCOUNT_BANDS)[number];
export const headcountBandSchema = z.enum(HEADCOUNT_BANDS);

// Upper bound of the band (used for k-anonymity / k-pool sizing defaults).
// `1000+` saturates at 5000 — the actual headcount is unknowable from a band
// so cap pessimistically for any computation that needs a number.
//
// TODO: wire into the k-pool default. Today `amber_aggregation_min_pool`
// is per-org and set by an admin; once the HR shell surfaces a "default
// by org size" preset, this lookup is the source of truth. Until then
// the helper is exported but unused — the dead-code lint passes because
// it's a side-effect-free function.
const BAND_CEILINGS: Readonly<Record<HeadcountBand, number>> = {
  '1-9': 9,
  '10-49': 49,
  '50-249': 249,
  '250-999': 999,
  '1000+': 5000,
};
export function headcountBandSize(band: HeadcountBand): number {
  return BAND_CEILINGS[band];
}

// PROJECT §4 Step 1 — primary contact (the day-to-day HR lead for the
// account) and escalation contact (the senior approver reached when the
// primary is unreachable). Both are name + work email pairs; we don't bind
// to a `users` row at signup time so the org can hand off to a real account
// later without a row-rewrite. The HR Admin from the same form is the
// initial `users` row.
export const orgContactSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().email().max(254),
});
export type OrgContact = z.infer<typeof orgContactSchema>;

export const SignupRequestSchema = z.object({
  organizationName: z.string().trim().min(2).max(120),
  adminName: z.string().trim().min(2).max(120),
  adminEmail: z.string().trim().toLowerCase().email().max(254),
  // PROJECT §4 Step 1 — headcount + contacts captured at signup.
  headcountBand: headcountBandSchema,
  primaryContact: orgContactSchema,
  escalationContact: orgContactSchema,
  dpaVersionAccepted: dpaVersionShape,
  hrSafetyContactAcknowledged: z.literal(true),
  // Turnstile token from the client widget. Required in production (the server
  // checks `TURNSTILE_SECRET`); optional in dev/test (the env var is unset).
  turnstileToken: z.string().optional(),
});
export type SignupRequest = z.infer<typeof SignupRequestSchema>;

// Slice 37 — body for `POST /api/auth/onboarding/complete`, the gated first-
// sign-in step for an HR Admin whose org was provisioned by a BesideU Admin
// (admin-provisioned orgs are created with NULL DPA / safety-contact fields,
// which the self-serve form would otherwise have captured). Same two
// acceptances as `SignupRequestSchema`, minus org/identity fields (those come
// from the onboarding token's bound user) and minus Turnstile (the magic link
// already proved control of the invited mailbox). The `onboardingToken` is the
// short-lived KV credential returned by `/auth/verify` as `onboarding_required`.
export const HrOnboardingSchema = z.object({
  // Same token-shape contract as the magic-link `VerifyBody` (a base64url
  // `generateSecureToken`), so malformed input is rejected at the body-parse
  // boundary rather than reaching the KV lookup.
  onboardingToken: z.string().min(32).max(128),
  dpaVersionAccepted: dpaVersionShape,
  hrSafetyContactAcknowledged: z.literal(true),
  // PROJECT §4 Step 1 — admin-provisioned orgs hit this endpoint to finish
  // onboarding; require the same headcount + primary/escalation contacts the
  // self-serve form captures, so the two paths produce congruent org rows.
  headcountBand: headcountBandSchema,
  primaryContact: orgContactSchema,
  escalationContact: orgContactSchema,
});
export type HrOnboardingRequest = z.infer<typeof HrOnboardingSchema>;

// Slice 31 — public response shape for `GET /api/signup/dpa` so the
// signup SPA renders the currently-active wording + version. `source`
// surfaces "constant" when no CGC-approved DB row exists yet (the
// pre-pilot state), which the marketing site can use to label the
// click-through as "placeholder" copy.
export interface SignupDpaResponse {
  source: 'db' | 'constant';
  version: string;
  body: string;
}

// Response is intentionally invariant for known/unknown / success/failure so
// the public endpoint can't be probed for email enumeration (DESIGN §4.6).
export interface SignupResponse {
  ok: true;
}
